import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '@components/AppIcon';
import Image from '@components/AppImage';
import Button from '@components/ui/Button';

const ServicesPreview = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetch('/api/pages/home');
        
        if (!response.ok) {
          throw new Error(`API Error: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        
        // Extract services_preview data from the response
        const servicesSection = data.homepage_sections?.find(
          section => section.type === 'services' && section.enabled !== false
        );
        
        if (!servicesSection) {
          setServices([]);
          setLoading(false);
          return;
        }
        
        // Filter and transform only enabled services
        const enabledServices = (servicesSection.services_preview || [])
          .filter(service => service.enabled !== false)
          .map(service => {
            // Extract description text from paragraph content
            let descriptionText = '';
            if (service.description_paragraph && service.description_paragraph.length > 0) {
              const firstParagraph = service.description_paragraph[0];
              if (firstParagraph.content && firstParagraph.content.length > 0) {
                descriptionText = firstParagraph.content
                  .filter(item => item.type === 'text')
                  .map(item => item.text)
                  .join(' ');
              }
            }
            
            // Extract features from key_features_list
            const features = service.key_features_list
              ? service.key_features_list.map(item => item.feature)
              : [];
            
            // Hard-coded icon mapping based on service name
            const iconMap = {
              'General Eye Check-Up': 'Activity',
              'Cataracts': 'Eye',
              'Glaucoma': 'Shield',
              'LASIK': 'Zap',
              'Retina': 'EyeOff',
              'Cornea': 'Droplet',
              'Keratoconus': 'Aperture',
              'Squint': 'ArrowRightLeft',
              'Refractive Errors': 'Glasses',
              'Optical Services': 'ShoppingBag'
            };
            
            return {
              id: service.id,
              title: service.service_name || '',
              description: descriptionText,
              icon: iconMap[service.service_name] || 'Eye',
              image: service.image ? `/storage/${service.image}` : null,
              features: features,
            };
          });
        
        setServices(enabledServices);
      } catch (err) {
        console.error('API Error:', err);
        setError(err.message);
        setServices([]);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  if (loading) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-text-secondary">Loading services...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-red-600">Error loading services: {error}</p>
        </div>
      </section>
    );
  }

  if (services.length === 0) {
    return null; // Don't render anything if no services are enabled
  }

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <div className="w-12 h-12 bg-[#08005f]/10 rounded-full flex items-center justify-center">
              <Icon name="Eye" size={24} color="#08005f" strokeWidth={2} />
            </div>
            <span className="font-heading font-semibold text-lg" style={{ color: "#08005f" }}>
              Our Services
            </span>
          </div>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl mb-4" style={{ color: "#08005f" }}>
            Comprehensive Eye Care Services
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            From routine check-ups to advanced surgical procedures, we offer complete eye care solutions 
            tailored to every stage of life and every vision need.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => (
            <div key={service.id} className="bg-[#f5f6fa] rounded-2xl medical-shadow overflow-hidden hover:bg-[#e8eafd] hover:medical-shadow medical-transition border border-[#08005f] border-opacity-10">
              {/* Service Image - Only show if available */}
              {service.image && (
                <div className="relative h-48 overflow-hidden">
                  <Image
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover"
                  />
                  {/* Service Icon */}
                  <div className="absolute bottom-4 right-4">
                    <div className="w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center border border-[#08005f]/10">
                      <Icon name={service.icon} size={24} color="#08005f" />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Service Icon if no image */}
              {!service.image && (
                <div className="p-6 pb-0">
                  <div className="w-12 h-12 bg-[#08005f]/10 rounded-full flex items-center justify-center">
                    <Icon name={service.icon} size={24} color="#08005f" />
                  </div>
                </div>
              )}

              {/* Service Content */}
              <div className="p-6">
                <div className="mb-4">
                  <h3 className="text-xl font-heading font-bold mb-1" style={{ color: "#08005f" }}>
                    {service.title}
                  </h3>
                  {service.description && (
                    <p className="text-text-secondary text-sm leading-relaxed">
                      {service.description}
                    </p>
                  )}
                </div>

                {/* Features - Only show if available */}
                {service.features && service.features.length > 0 && (
                  <div className="mb-4">
                    <div className="text-xs text-text-secondary mb-2">Key Features:</div>
                    <div className="flex flex-wrap gap-1">
                      {service.features.slice(0, 3).map((feature, index) => (
                        <span key={index} className="text-xs bg-white text-text-secondary px-2 py-1 rounded border border-[#08005f] border-opacity-10">
                          {feature}
                        </span>
                      ))}
                      {service.features.length > 3 && (
                        <span className="text-xs text-[#eb2227]">+{service.features.length - 3} more</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* View All Services CTA */}
        <div className="text-center mt-12">
          <Link to="/services-comprehensive-eye-care-universe">
            <Button
              variant="outline"
              size="lg"
              iconName="ArrowRight"
              iconPosition="right"
              className="border-[#08005f] text-[#08005f] hover:bg-[#eb2227]"
            >
              View All Services & Specialties
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServicesPreview;